/**
 * /dashboard/timesheet/admin/projects — HR-admin project catalogue
 * (G3 T.3c; SOP §1.2; Phase 3b — 2026-06-01).
 *
 * RBAC: list + create + edit + soft-delete are HR-admin server-side
 * (company_admin / hr_manager). The page hard role-gates the entire
 * surface — a non-admin who lands here is redirected to /dashboard. The
 * backend is the real boundary and re-checks every request.
 *
 * Per SOP §3.2 this admin catalogue lives under Settings → Workspace in
 * the long term; until that menu lands the legacy /timesheet/admin URL
 * is preserved so existing bookmarks resolve.
 *
 * Migration note: port of
 * frontend/src/app/(dashboard)/dashboard/timesheet/admin/projects/page.tsx.
 * Swaps:
 *   - "use client" dropped
 *   - useRouter (next/navigation) → useNavigate (@tanstack/react-router)
 *   - next/link → @tanstack/react-router Link (typed)
 */

import { useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate , useParams} from "@tanstack/react-router";
import axios from "axios";
import {
  AlertTriangle,
  FolderKanban,
  PackageOpen,
  RefreshCw,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import type { Role } from "@/types/auth";
import { toApiError } from "@/lib/api";
import { useProjects } from "@/hooks/use-timesheet";
import { formatPaise } from "@/lib/expense-format";
import type { Project } from "@/types/timesheet";
import { TimeAndLeaveShell } from "@/components/shared/time-and-leave-shell";
import { StatusBadge } from "@/components/shared/status-badge";
import { TimesheetFeatureGate } from "@/components/timesheet/timesheet-feature-gate";
import { ProjectFormDialog } from "@/components/timesheet/project-form-dialog";
import { ProjectDeleteButton } from "@/components/timesheet/project-delete-button";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/timesheet/admin/projects/")({
  component: ProjectsAdminRoute,
});

// Mirrors the backend RBAC on POST/PATCH/DELETE /api/v1/projects
// (company_admin, hr_manager). Gates the entire admin surface.
const HR_ADMINS = new Set<Role>(["company_admin", "hr_manager"]);

const LOADING_SKELETON_KEYS = [
  "pa-sk-0",
  "pa-sk-1",
  "pa-sk-2",
  "pa-sk-3",
] as const;

function statusOf(err: unknown): number | undefined {
  return axios.isAxiosError(err) ? err.response?.status : undefined;
}

function isNotFound(err: unknown): boolean {
  return statusOf(err) === 404;
}

function isForbidden(err: unknown): boolean {
  return statusOf(err) === 403;
}

/** "₹2,500/hr" or "—" when the project has no default rate. */
function fmtRate(paise: number | null): string {
  if (paise == null) return "—";
  return `${formatPaise(paise)}/hr`;
}

function ProjectsAdminRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const canAdmin = !!user?.role && HR_ADMINS.has(user.role);

  const [includeInactive, setIncludeInactive] = useState(false);
  const q = useProjects({ include_inactive: includeInactive });

  // Sort by code asc — the natural admin ordering (codes are stable
  // identifiers, names can change). Server may return in any order; this
  // is the canonical sort so a refetch / cached read both render the same.
  const rows = useMemo<Project[]>(() => {
    const items = q.data?.items ?? [];
    return [...items].sort((a, b) =>
      a.code.localeCompare(b.code),
    );
  }, [q.data]);

  // Non-admin → bounce. A non-admin can't action anything on this page,
  // the entire surface is admin-only; routing back to /dashboard keeps
  // them where the rest of the app is available. Wait for the auth
  // context to hydrate so we don't bounce a still-loading admin.
  if (!loading && user && !canAdmin) {
    void navigate({ to: "/app/$tenantSlug", params: { tenantSlug }, replace: true });
    return null;
  }

  const endpointMissing = isNotFound(q.error);
  const forbidden = isForbidden(q.error);
  const hardError = q.error && !endpointMissing && !forbidden;

  return (
    <TimeAndLeaveShell
      title="Timesheet projects"
      description="Projects employees can log hours against."
    >
      <TimesheetFeatureGate>
        <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between sm:gap-4">
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-input accent-primary"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            Show inactive
          </label>
          {canAdmin && <ProjectFormDialog />}
        </div>

        {q.isLoading && (
          <div className="space-y-3">
            {LOADING_SKELETON_KEYS.map((k) => (
              <Skeleton key={k} className="h-16 w-full" />
            ))}
          </div>
        )}

        {endpointMissing && (
          <Card>
            <CardContent className="flex flex-col items-center gap-3 py-14 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent text-accent-foreground">
                <PackageOpen className="h-6 w-6" />
              </span>
              <div className="space-y-1">
                <p className="text-base font-medium text-gray-900">
                  Projects aren&apos;t available yet
                </p>
                <p className="max-w-md text-sm text-muted-foreground">
                  The timesheet API is still being deployed. This page
                  will populate automatically once it is live — no action
                  needed on your side.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {forbidden && (
          <Card>
            <CardContent className="flex flex-col items-center gap-3 py-14 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent text-accent-foreground">
                <AlertTriangle className="h-6 w-6" />
              </span>
              <p className="text-sm text-muted-foreground">
                You don&apos;t have access to projects.
              </p>
            </CardContent>
          </Card>
        )}

        {hardError && (
          <Alert variant="destructive">
            <AlertTitle>Could not load projects</AlertTitle>
            <AlertDescription className="flex flex-col gap-2">
              <span>{toApiError(q.error).message}</span>
              <span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => q.refetch()}
                  className="gap-1.5"
                >
                  <RefreshCw className="h-4 w-4" />
                  Retry
                </Button>
              </span>
            </AlertDescription>
          </Alert>
        )}

        {!q.isLoading &&
          !endpointMissing &&
          !forbidden &&
          !hardError &&
          rows.length === 0 && (
            <Card>
              <CardContent className="flex flex-col items-center gap-4 py-14 text-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent text-accent-foreground">
                  <FolderKanban className="h-6 w-6" />
                </span>
                <div className="space-y-1">
                  <p className="text-base font-medium text-gray-900">
                    No projects yet
                  </p>
                  <p className="max-w-sm text-sm text-muted-foreground">
                    Add the first project your team will log hours
                    against. You can configure billing and assignments
                    per project.
                  </p>
                </div>
                <ProjectFormDialog />
              </CardContent>
            </Card>
          )}

        {!q.isLoading &&
          !endpointMissing &&
          !forbidden &&
          !hardError &&
          rows.length > 0 && (
            <>
              {/* md+ : a clean shadcn table — desktop / tablet. */}
              <Card className="hidden md:block">
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Code</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Client</TableHead>
                        <TableHead>Billable</TableHead>
                        <TableHead>Rate</TableHead>
                        <TableHead>Assignment</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">
                          Actions
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.map((p) => (
                        <TableRow key={p.id}>
                          <TableCell className="whitespace-nowrap font-mono text-sm font-medium text-gray-900">
                            <Link
                              to="/app/$tenantSlug/timesheet/admin/projects/$id"
                              params={{tenantSlug, id: String(p.id)}}
                              className="hover:underline"
                            >
                              {p.code}
                            </Link>
                          </TableCell>
                          <TableCell className="font-medium text-gray-900">
                            {p.name}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {p.client_name?.trim() ? p.client_name : "—"}
                          </TableCell>
                          <TableCell>
                            {p.is_billable ? (
                              <Badge variant="secondary">Billable</Badge>
                            ) : (
                              <span className="text-sm text-muted-foreground">
                                No
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                            {fmtRate(p.billing_rate_paise)}
                          </TableCell>
                          <TableCell className="text-sm">
                            {p.requires_assignment ? (
                              <Badge variant="outline">Restricted</Badge>
                            ) : (
                              <span className="text-muted-foreground">
                                Open
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <StatusBadge
                              tone={p.is_active ? "success" : "neutral"}
                              label={p.is_active ? "Active" : "Inactive"}
                            />
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                asChild
                                size="sm"
                                variant="ghost"
                                className="gap-1.5"
                              >
                                <Link
                                  to="/app/$tenantSlug/timesheet/admin/projects/$id"
                                  params={{tenantSlug, id: String(p.id)}}
                                >
                                  Open
                                </Link>
                              </Button>
                              <ProjectFormDialog existing={p} />
                              {p.is_active && (
                                <ProjectDeleteButton project={p} />
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* < md : stacked cards — mobile (360px PWA target). */}
              <div className="space-y-3 md:hidden">
                {rows.map((p) => (
                  <Card key={p.id}>
                    <CardHeader>
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0">
                          <CardTitle className="text-base">
                            <Link
                              to="/app/$tenantSlug/timesheet/admin/projects/$id"
                              params={{tenantSlug, id: String(p.id)}}
                              className="hover:underline"
                            >
                              {p.name}
                            </Link>
                          </CardTitle>
                          <p className="mt-1 font-mono text-xs text-muted-foreground">
                            {p.code}
                          </p>
                          {p.client_name?.trim() && (
                            <p className="mt-1 text-sm text-muted-foreground">
                              Client: {p.client_name}
                            </p>
                          )}
                          <p className="mt-2 text-xs text-muted-foreground">
                            {p.is_billable ? "Billable" : "Non-billable"}
                            {" · "}
                            {fmtRate(p.billing_rate_paise)}
                            {" · "}
                            {p.requires_assignment
                              ? "Restricted"
                              : "Open access"}
                          </p>
                        </div>
                        <StatusBadge
                          tone={p.is_active ? "success" : "neutral"}
                          label={p.is_active ? "Active" : "Inactive"}
                        />
                      </div>
                    </CardHeader>
                    <CardContent className="flex flex-wrap justify-end gap-2 pt-0">
                      <Button
                        asChild
                        size="sm"
                        variant="ghost"
                        className="gap-1.5"
                      >
                        <Link
                          to="/app/$tenantSlug/timesheet/admin/projects/$id"
                          params={{tenantSlug, id: String(p.id)}}
                        >
                          Open
                        </Link>
                      </Button>
                      <ProjectFormDialog existing={p} />
                      {p.is_active && <ProjectDeleteButton project={p} />}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}
      </TimesheetFeatureGate>
    </TimeAndLeaveShell>
  );
}
